import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import { CheckoutPolicySettings } from '../checkout/CheckoutPolicySettings';
import { CheckoutRequestsList } from '../checkout/CheckoutRequestsList';
import { defaultCheckoutPolicy } from '../../utils/checkoutUtils';
import { useApi } from '../Store/ApiContext';
import { constantsErrorMessage, editSuccessMessage, addSuccessMessage } from '../../constants/global';
import { use } from 'react';

export const CheckoutManagementPage = () => {
  const { t } = useTranslation();
  const { getData, responseApi, quickEditTable } = useApi();

  const [policy, setPolicy] = useState(null);
  const [requests, setRequests] = useState([]);
  const [pagination, setPagination] = useState({});
  const [activeTab, setActiveTab] = useState('requests');
  const [loading, setLoading] = useState(false);
  const [policyLoading, setPolicyLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Load initial data
  useEffect(() => {
    // add title page
    document.title = t("manageCheckout");
    loadCheckoutPolicy();
  }, []);


  const loadCheckoutRequests = async () => {
    try {
      setLoading(true);
      const response = await getData(null, `checkout/requests?page=${currentPage}`);
      if (response?.results) {
        setRequests(response.results);
        setPagination(response.pagination);
      }
    } catch (error) {
      console.error('Error loading checkout requests:', error);
      enqueueSnackbar(<>{t(constantsErrorMessage)}<br />{error.message}</>, { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const loadCheckoutPolicy = async () => {
    try {
      setPolicyLoading(true);
      const response = await getData(null, `checkout/policy`);
      if (response?.policy) {
        setPolicy(response.policy);
      }
    } catch (error) {
      console.error('Error loading checkout policy:', error);
      // Keep default policy if API fails
    } finally {
      setPolicyLoading(false);
    }
  };

  const handleSubmitRequest = async (requestData) => {
    try {
      setLoading(true);

      const response = await responseApi('post', requestData, 'checkout/requests');

      if (response?.message === "success") {
        enqueueSnackbar(<>{t(addSuccessMessage)}</>, { variant: "success" });
        await loadCheckoutRequests();
      } else {
        enqueueSnackbar(<>{t(constantsErrorMessage)}<br />{response?.message}</>, { variant: "error" });
      }
    } catch (error) {
      console.error('Error submitting checkout request:', error);
      enqueueSnackbar(<>{t(constantsErrorMessage)}<br />{error.message}</>, { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleApproveRequest = async (id) => {
    try {
      const response = await quickEditTable('checkout/requests', { status: 'approved' }, id, setRequests);
      if (response && response.message !== "success") {
        enqueueSnackbar(<>{t(constantsErrorMessage)}<br />{response.message}</>, { variant: "error" });
      }
    } catch (error) {
      console.error('Error approving checkout request:', error);
      enqueueSnackbar(<>{t(constantsErrorMessage)}<br />{error.message}</>, { variant: "error" });
    }
  };

  const handleRejectRequest = async (id) => {
    try {
      // const rejectionReason = prompt(t('pleaseEnterTheCancellationReason'));

      // if (!rejectionReason) return;

      const response = await quickEditTable('checkout/requests', {
        status: 'rejected',
        rejectionReason: ""
      }, id, setRequests);

      if (response && response.message !== "success") {
        enqueueSnackbar(<>{t(constantsErrorMessage)}<br />{response.message}</>, { variant: "error" });
      }
    } catch (error) {
      console.error('Error rejecting checkout request:', error);
      enqueueSnackbar(<>{t(constantsErrorMessage)}<br />{error.message}</>, { variant: "error" });
    }
  };

  const handleSavePolicy = async (updatedPolicy) => {
    try {
      setPolicyLoading(true);
      const response = await responseApi('PUT', updatedPolicy, 'checkout/policy');

      if (response?.message === "success") {
        setPolicy(response.results || updatedPolicy);
        enqueueSnackbar(<>{t(editSuccessMessage)}</>, { variant: "success" });
      } else {
        enqueueSnackbar(<>{t(constantsErrorMessage)}<br />{response?.message}</>, { variant: "error" });
      }
    } catch (error) {
      console.error('Error saving checkout policy:', error);
      enqueueSnackbar(<>{t(constantsErrorMessage)}<br />{error.message}</>, { variant: "error" });
    } finally {
      setPolicyLoading(false);
    }
  };
  useEffect(() => {
    loadCheckoutRequests();
  }, [currentPage])


  return (
    <section className='bg-babyblue-50 big-card container-fluid'>
      <div className="pt-4 pb-3 px-2 mx-3">
        <div className="">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-slate-900 mb-2">
              {t('manageCheckout')}
            </h1>
            <p className="text-slate-600">
              {t('manageCheckoutDescription')}
            </p>
          </div>

          {/* Tab Navigation */}
          <div className="mb-6">
            <div className="flex space-x-1 rtl:space-x-reverse bg-slate-100 rounded-xl p-1">
              <button
                onClick={() => setActiveTab('requests')}
                className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${activeTab === 'requests'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
                  }`}
              >
                {t('checkoutRequests')} ({pagination.totalItems})
              </button>
              <button
                onClick={() => setActiveTab('settings')}
                className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${activeTab === 'settings'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
                  }`}
              >
                {t('policySettings')}
              </button>
            </div>
          </div>

          {/* Content */}
          {activeTab === 'requests' ? (
            <CheckoutRequestsList
              requests={requests}
              pagination={pagination}
              onApprove={handleApproveRequest}
              onReject={handleRejectRequest}
              loading={loading}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
            />
          ) : (
            <CheckoutPolicySettings
              policy={policy}
              onSave={handleSavePolicy}
              loading={policyLoading}
            />
          )}

          {/* Demo Section - How to Use */}
          <div className="mt-8 bg-blue-50 rounded-2xl p-6 border border-blue-200">
            <h3 className="text-lg font-semibold text-blue-900 mb-4">
              {t('howToUseTitle')}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-4 border border-blue-200">
                <h4 className="font-semibold text-green-700 mb-3">
                  {t('earlyCheckoutHeader')}
                </h4>
                <div className="text-sm text-slate-700 space-y-2">
                  <p><strong>{t('example')}:</strong></p>
                  <p>{t('guestBookedNights', { nights: 3, price: 1500 })}</p>
                  <p>{t('wantsToLeaveEarly')}</p>
                  <p className="text-green-600 font-medium">{t('exampleEarlyRefund')}</p>
                  <p className="text-blue-600 text-xs">
                    {t('roomAvailableSameDay')}
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-xl p-4 border border-blue-200">
                <h4 className="font-semibold text-orange-700 mb-3">
                  {t('lateCheckoutHeader')}
                </h4>
                <div className="text-sm text-slate-700 space-y-2">
                  <p><strong>{t('example')}:</strong></p>
                  <p>{t('scheduledCheckout', { time: '11:00 AM' })}</p>
                  <p>{t('requestedCheckoutExample', { time: '3:00 PM', hours: 4 })}</p>
                  <p className="text-orange-600 font-medium">{t('exampleLateFee')}</p>
                  <p className="text-blue-600 text-xs">
                    {t('freeGracePeriod')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};